import type { admin } from '@repletoryreach/sdk-api';

/** The order the groups read in; a region the page does not know yet still shows, after these. */
export const REGION_ORDER: readonly string[] = ['south_africa', 'africa', 'widely_used'];

/** How well the AI writes a language; `review` asks for a native speaker to read the fixed wording. */
export const QUALITIES = ['strong', 'good', 'review'] as const;
export type Quality = (typeof QUALITIES)[number];

/** English, which every chatbot falls back to, cannot be switched off. */
export const ENGLISH = 'en';

/** English and Afrikaans lines are written in the code, so there is nothing to translate or edit here. */
export const linesInCode = (code: string): boolean => code === 'en' || code === 'af';

/** The service's limits, repeated so the drawer can say so before saving. */
export const NAME_MAX = 60;
export const NATIVE_MAX = 20;
export const WORDS_MAX = 40;
export const WORD_LENGTH_MAX = 40;
/** Meta's template language code: `zu`, `en_ZA`, `pt_BR`. */
export const META_CODE = /^[a-z]{2,3}(_[A-Z]{2})?$/;
/** AWS Transcribe's language code: `zu-ZA`, `sw-KE`. */
export const SPEECH_CODE = /^[a-z]{2,3}-[A-Z]{2}$/;

/** Languages in reading order: the owner's sort order, then code. */
export function sortLanguages(list: readonly admin.AdminLanguage[]): admin.AdminLanguage[] {
  return [...list].sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code));
}

/** The list in groups by region, each group in reading order. */
export function byRegion(list: readonly admin.AdminLanguage[]): Array<{ region: string; languages: admin.AdminLanguage[] }> {
  const groups = new Map<string, admin.AdminLanguage[]>();
  for (const l of sortLanguages(list)) groups.set(l.region, [...(groups.get(l.region) ?? []), l]);
  const known = REGION_ORDER.filter((r) => groups.has(r));
  const unknown = Array.from(groups.keys()).filter((r) => !REGION_ORDER.includes(r));
  return [...known, ...unknown].map((region) => ({ region, languages: groups.get(region)! }));
}

/** The lines a customer would still see in English: the built-in lines not translated from the current English. */
export const missingLines = (l: admin.AdminLanguage): number => Math.max(0, l.wordings.total - l.wordings.translated);

/** Badge colour per quality; an unknown one is plain. */
export function qualityColour(quality: string): string {
  return quality === 'strong' ? 'green' : quality === 'good' ? 'blue' : quality === 'review' ? 'orange' : 'gray';
}

/** `{{person}}`, `{{contact.name ?? there}}`: the name is the part before any `??`, which may itself be translated. */
const PLACEHOLDER = /\{\{\s*([^}?]+?)\s*(?:\?\?[^}]*)?\}\}/g;

function placeholderNames(text: string): Set<string> {
  return new Set(Array.from(text.matchAll(PLACEHOLDER), (m) => m[1].trim()));
}

/**
 * The `{{...}}` parts a translation has lost or added, as the service checks before saving: the names must match the
 * English exactly. Empty lists when the line is fine.
 */
export function placeholderProblems(english: string, text: string): { missing: string[]; extra: string[] } {
  const wanted = placeholderNames(english);
  const given = placeholderNames(text);
  return {
    missing: [...wanted].filter((n) => !given.has(n)).map((n) => `{{${n}}}`),
    extra: [...given].filter((n) => !wanted.has(n)).map((n) => `{{${n}}}`)
  };
}
